import PageTitle from '@/components/PageTitle';
import ThemeWrapper from '@/components/ThemeWrapper';
import AppLayout from '@/layouts/AppLayout';
import { usePage } from '@inertiajs/react';
import { ExternalLink, Video } from 'lucide-react';
import React from 'react';

interface SessionItem {
    name: string;
    schedule: string;
    description?: string;
    link_url: string;
    link_text?: string;
}

interface PageConfig {
    title: string;
    sessions: SessionItem[];
    questions_link?: {
        text: string;
        url: string;
    };
}

interface PageProps {
    static_page: {
        id: number;
        name: string;
        slug: string;
        config: PageConfig;
    };
}

const WebinarsEliteContent: React.FC = () => {
    const { static_page } = usePage<{ static_page: PageProps['static_page'] }>().props;
    const config = static_page.config;

    return (
        <AppLayout>
            <PageTitle title={static_page.name} />
            <div className="mx-auto max-w-5xl space-y-6">
                <h1 className="text-4xl font-bold text-gray-900 dark:text-white sm:text-5xl">
                    {config.title}
                </h1>

                <div className="grid gap-4 md:grid-cols-2">
                    {config.sessions.map((session, i) => (
                        <div
                            key={i}
                            className="flex flex-col rounded-xl border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 shadow-sm"
                        >
                            <div className="flex-1 space-y-2 p-6">
                                <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                                    {session.name}
                                </h3>
                                <p className="text-base text-gray-500 dark:text-neutral-400">{session.schedule}</p>
                                {session.description && (
                                    <p className="text-base text-gray-700 dark:text-neutral-300">{session.description}</p>
                                )}
                            </div>
                            <div className="p-6 pt-0">
                                <a
                                    href={session.link_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="primary-button flex w-full items-center justify-center gap-2 uppercase tracking-wide"
                                >
                                    <Video className="h-4 w-4" />
                                    {session.link_text ?? 'ENTRAR A LA SALA'}
                                </a>
                            </div>
                        </div>
                    ))}
                </div>

                {config.questions_link && (
                    <div className="flex justify-center">
                        <a
                            href={config.questions_link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 px-6 py-3 text-base font-semibold text-gray-700 dark:text-neutral-300 transition hover:bg-gray-50 dark:hover:bg-neutral-700"
                        >
                            <ExternalLink className="h-4 w-4" />
                            {config.questions_link.text}
                        </a>
                    </div>
                )}
            </div>
        </AppLayout>
    );
};

const WebinarsElite: React.FC = () => (
    <ThemeWrapper>
        <WebinarsEliteContent />
    </ThemeWrapper>
);

export default WebinarsElite;
